import { useAuth } from '@/api/auth'
import { getRolloutUebersicht } from '@/api/priceRollout'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { AusverkaufAbzeichen } from '@/components/AusverkaufAbzeichen';
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useResolvedImages } from '@/api/produktbilder';
import { ProductPhotoHint } from '@/components/ProductPhotoHint';
import { useGaSales } from "@/api/dashboard";
import { VerkaufZelle } from "./VerkaufZelle";
import { BestandMitVorab } from "@/components/vorab/BestandMitVorab";
import { Download, Plus, Folder, ChevronRight, Tag, Power } from "lucide-react";
import { bulkPricing, bulkStatus, type StockItem } from "@/api/stock";
import { katalogArtikelQuery, katalogOrdnerQuery } from "./katalog-queries";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FolderTree } from "@/components/FolderTree";
import { catalogMatchRank } from "@/lib/catalog-search";
import {
  buildTreeFromFolders,
  buildFolderPathMap,
  countsBySubtree,
  findFolderNode,
  folderAncestorIds,
  katalogBaum,
  prettyFolderLabel,
  subtreeFolderIds,
} from "@/lib/folderTree";

// EK = supplier_price (Grundartikel › Preise › „Einkaufspreis (EK)“); 0 gilt dort als nicht gepflegt.
function fmtEK(n: string | number | null | undefined): string {
  if (n === null || n === undefined || n === "") return "—";
  const num = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(num) || num <= 0) return "—";
  // Schrauben kosten Bruchteile eines Cents: bis zu 4 Nachkommastellen statt 0,01 €.
  return num.toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

function stockState(item: StockItem): { kind: "ok" | "low" | "crit" | "draft"; label: string } {
  if (!item.is_active) return { kind: "draft", label: "Entwurf" };
  if (item.quantity === 0) return { kind: "crit", label: "Kritisch" };
  if (item.quantity_alert && item.quantity <= item.quantity_alert) {
    return { kind: "low", label: "Niedrig" };
  }
  return { kind: "ok", label: "Aktiv" };
}

const STOCK_PILL_CLASSES: Record<"ok" | "low" | "crit" | "draft", string> = {
  ok: "bg-success-soft text-success",
  low: "bg-warn-soft text-warn",
  crit: "bg-coral-soft text-coral",
  draft: "bg-panel-2 text-ink-2",
};

// Max. gerenderte Tabellenzeilen. Ohne Deckelung rendert die Tabelle bis zu ~1.800
// komplexe Zeilen synchron pro Filter-/Tastendruck → 300–500 ms Main-Thread-Block.
// Die alte (server-seitige) Suche lieferte ebenfalls max. 200 Zeilen.
const RENDER_CAP = 300;

export default function Catalog() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  // Ordner und Suche stehen in der Adresse (?ordner=<folder_id>&q=<suche>): Browser-Zurück,
  // Neuladen und der Rückweg aus dem Grundartikel landen so wieder im selben Ordner.
  const [searchParams, setSearchParams] = useSearchParams();
  const qParam = searchParams.get("q") ?? "";
  const ordnerParam = Number(searchParams.get("ordner"));
  const ordnerId = Number.isInteger(ordnerParam) && ordnerParam > 0 ? ordnerParam : null;
  // Eingabe bleibt lokal (sofort responsiv) und wird sofort in die Adresse geschrieben;
  // aus der Adresse uebernommen nur beim Start und bei Browser-Zurueck/-Vor (POP).
  const [searchInput, setSearchInputState] = useState(qParam);
  const [search, setSearch] = useState(qParam);
  const [seenKey, setSeenKey] = useState(location.key);
  if (location.key !== seenKey) {
    setSeenKey(location.key);
    if (navigationType === "POP" && qParam !== searchInput) setSearchInputState(qParam);
  }

  function setSearchInput(value: string) {
    setSearchInputState(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("q", value);
        else next.delete("q");
        return next;
      },
      { replace: true },
    );
  }
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showPricingSheet, setShowPricingSheet] = useState(false);
  const [pricingMode, setPricingMode] = useState<"delta_pct" | "fixed">("delta_pct");
  const [pricingDelta, setPricingDelta] = useState("");
  const [pricingFixed, setPricingFixed] = useState("");
  const [pricingPending, setPricingPending] = useState(false);
  const [statusPending, setStatusPending] = useState(false);
  const queryClient = useQueryClient();

  // Suche entprellen: Eingabe bleibt sofort responsiv, die (teure) Filterung +
  // Neu-Rendern laeuft erst 200 ms nach dem letzten Tastendruck.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: folders, isPending: foldersPending } = useQuery(katalogOrdnerQuery);

  const { data: items, isLoading, isError } = useQuery(katalogArtikelQuery);

  const allItems = useMemo(() => items ?? [], [items]);

  // folderId -> Pfad-Segmente
  const pathMap = useMemo(
    () => (folders ? buildFolderPathMap(folders) : new Map<number, string[]>()),
    [folders],
  );

  // Katalog zeigt nur den Hauptordner (mit Unterordnern) und „Gelöscht“ – auch Ordner ohne aktiven
  // Grundartikel, damit sichtbar ist, dass der Ordner aus DR uebertragen wurde. Die Artikel-Liste und
  // der Ordner-Zaehler bleiben Grundartikel-scoped (allItems). Ohne Hauptordner: ganzer Baum.
  const { roots: tree, hauptordner } = useMemo(
    () => katalogBaum(folders ? buildTreeFromFolders(folders) : []),
    [folders],
  );

  // Gewaehlter Ordner aus der Adresse, per id. Solange die Ordner laden, bleibt ?ordner stehen;
  // wirksam ist nur eine id im sichtbaren Baum – sonst der Hauptordner (fehlt er: „Alle Ordner“).
  const folderNode = useMemo(
    () => findFolderNode(tree, ordnerId) ?? hauptordner,
    [tree, ordnerId, hauptordner],
  );
  const effectiveId = folderNode ? folderNode.id : null;
  const folderIdSet = useMemo(
    () => (folderNode ? new Set(subtreeFolderIds(folderNode)) : null),
    [folderNode],
  );
  // Brotkrumen: Vorfahren + gewaehlter Ordner, je mit id (Namen nur zur Anzeige).
  const crumbs = useMemo(
    () =>
      effectiveId === null
        ? []
        : [...folderAncestorIds(tree, effectiveId), effectiveId].map((id) => ({
            id,
            name: pathMap.get(id)?.at(-1) ?? "",
          })),
    [tree, effectiveId, pathMap],
  );

  // Ordnerwahl (Baum, Brotkrumen) beginnt ohne Suche: ein alter Begriff verschluckte sonst den
  // neuen Ordner. Browser-Zurueck bringt die alte Suche ueber die Adresse wieder.
  function selectFolder(id: number | null) {
    setSearchInputState("");
    setSearch("");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id != null) next.set("ordner", String(id));
      else next.delete("ordner");
      next.delete("q");
      return next;
    });
  }

  // Ordnerwechsel – egal ob Baum, Brotkrumen, Browser-Zurueck oder spaet geladene Ordner –
  // leert die Haekchen, sonst wirkten Preis-/Status-Aktionen auf unsichtbare Artikel.
  useEffect(() => {
    setSelected(new Set());
  }, [effectiveId]);

  const countsById = useMemo(
    () =>
      countsBySubtree(
        tree,
        allItems.map((i) => i.folder_id).filter((x): x is number => x != null),
      ),
    [tree, allItems],
  );

  // Pfad je Item fuer die Ordner-Spalte; unter dem Hauptordner ohne dessen eigenen Namen.
  function itemPath(item: StockItem): string[] {
    const path = item.folder_id != null ? pathMap.get(item.folder_id) ?? [] : [];
    return hauptordner && path.length > 1 && path[0] === hauptordner.name ? path.slice(1) : path;
  }

  const folderItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = folderIdSet
      ? allItems.filter((item) => item.folder_id != null && folderIdSet.has(item.folder_id))
      : allItems;
    if (!term) return filtered;
    return filtered
      .map((item) => ({ item, rank: catalogMatchRank(item, term) }))
      .filter((match): match is { item: StockItem; rank: number } => match.rank !== null)
      .sort((a, b) => a.rank - b.rank)
      .map((match) => match.item);
  }, [allItems, folderIdSet, search]);
  // Die aktuelle Eingabe gilt auch vor Ablauf der Such-Entprellung.
  function katalogHerkunft() {
    const params = new URLSearchParams(searchParams);
    if (searchInput) params.set("q", searchInput);
    else params.delete("q");
    const qs = params.toString();
    return { liste: `/catalog${qs ? `?${qs}` : ""}` };
  }

  const visibleItems = folderItems.slice(0, RENDER_CAP);
  // Backend ordnet article_general den beiden benoetigten Scopes zu.
  const { user } = useAuth();
  const rolloutErlaubt = !!user?.permissions?.article_general;
  const skuBloecke = Array.from({ length: Math.ceil(visibleItems.length / 20) }, (_, i) =>
    visibleItems.slice(i * 20, i * 20 + 20).map(item => item.sku));
  const rolloutQueries = useQueries({ queries: skuBloecke.map(skus => ({
    queryKey: ['rollout-uebersicht', skus],
    queryFn: ({ signal }) => getRolloutUebersicht(skus, signal),
    enabled: rolloutErlaubt, staleTime: 5 * 60_000,
  })) });
  const photos = useResolvedImages(visibleItems.map(item => item.sku));
  const photosBySku = new Map(photos.data?.results.map(row => [row.sku, row]));
  // Verkauf 30 T.: ein Sammelaufruf fuer genau die sichtbaren Zeilen.
  const sales = useGaSales(visibleItems.map((item) => item.id));
  const salesById = new Map(sales.data?.items.map((row) => [row.id, row]));

  function toggleSelect(sku: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === folderItems.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(folderItems.map((i) => i.sku)));
    }
  }

  async function applyPricing() {
    if (selected.size === 0) return;
    const skus = [...selected];
    setPricingPending(true);
    try {
      let payload: { price_delta_pct?: number; fixed_price?: number };
      if (pricingMode === "delta_pct") {
        const v = Number(pricingDelta);
        if (Number.isNaN(v) || pricingDelta.trim() === "") {
          toast.error("Bitte einen gueltigen Prozentwert eingeben");
          setPricingPending(false);
          return;
        }
        payload = { price_delta_pct: v };
      } else {
        const v = Number(pricingFixed);
        if (Number.isNaN(v) || pricingFixed.trim() === "" || v < 0) {
          toast.error("Bitte einen gueltigen Festpreis eingeben");
          setPricingPending(false);
          return;
        }
        payload = { fixed_price: v };
      }
      const result = await bulkPricing(skus, payload);
      void queryClient.invalidateQueries({ queryKey: ["catalog-grundartikel"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-stock"] });
      setShowPricingSheet(false);
      setPricingDelta("");
      setPricingFixed("");
      setSelected(new Set());
      toast.success(
        pricingMode === "delta_pct"
          ? `${result.updated} Preise um ${pricingDelta}% angepasst`
          : `${result.updated} Preise auf ${pricingFixed} EUR gesetzt`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk-Pricing fehlgeschlagen");
    } finally {
      setPricingPending(false);
    }
  }

  async function applyStatus(is_active: boolean) {
    if (selected.size === 0) return;
    const skus = [...selected];
    setStatusPending(true);
    try {
      const result = await bulkStatus(skus, is_active);
      void queryClient.invalidateQueries({ queryKey: ["catalog-grundartikel"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory-stock"] });
      setSelected(new Set());
      toast.success(
        `${result.updated} Artikel ${is_active ? "aktiviert" : "deaktiviert"}`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Bulk-Status fehlgeschlagen");
    } finally {
      setStatusPending(false);
    }
  }

  // Ohne Ordner keine Liste: sonst stuenden kurz alle Artikel da, bevor der Hauptordner greift.
  const listLoading = isLoading || foldersPending;

  return (
    // Ab lg genau Bildschirmhoehe (minus Topbar h-14): die Seite selbst scrollt nicht mehr, nur die
    // Artikelliste in ihrem eigenen Bereich – der Ordnerbaum links bleibt dadurch immer stehen.
    // (main in Layout hat overflow-auto ohne Hoehe; position: sticky greift dort nicht.)
    <div className="p-6 space-y-4 lg:h-[calc(100vh-3.5rem)] lg:flex lg:flex-col">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-ink">Produktkatalog</h1>
          <p className="text-sm text-ink-2 mt-1">
            {crumbs.length === 0
              ? `Alle Ordner · ${folderItems.length.toLocaleString("de-DE")} Artikel`
              : `Ordner: ${crumbs.map((c) => prettyFolderLabel(c.name)).join(" / ")} · ${folderItems.length.toLocaleString("de-DE")} ${search.trim() ? "Treffer" : "Artikel"}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5">
            <Download className="size-3.5" />
            CSV Export
          </Button>
          <Button size="sm" className="gap-1.5" disabled>
            <Plus className="size-3.5" />
            Neues Produkt
          </Button>
        </div>
      </header>

      {/* Breadcrumb */}
      <nav aria-label="Ordnerpfad" className="flex items-center gap-1.5 text-sm flex-wrap">
        {hauptordner ? (
          <Folder className="size-3.5 text-ink-2" aria-hidden />
        ) : (
          <button
            onClick={() => selectFolder(null)}
            className={
              crumbs.length === 0
                ? "inline-flex items-center gap-1 font-semibold text-ink"
                : "inline-flex items-center gap-1 text-primary hover:underline"
            }
          >
            <Folder className="size-3.5" />
            Alle Ordner
          </button>
        )}
        {crumbs.map((crumb, i) => (
          <span key={crumb.id} className="inline-flex items-center gap-1.5">
            {(i > 0 || !hauptordner) && <ChevronRight className="size-3 text-muted" />}
            <button
              onClick={() => selectFolder(crumb.id)}
              className={
                i === crumbs.length - 1
                  ? "font-semibold text-ink"
                  : "text-primary hover:underline"
              }
            >
              {prettyFolderLabel(crumb.name)}
            </button>
          </span>
        ))}
      </nav>

      {/* Two-column: tree | content */}
      <div
        data-testid="katalog-bereich"
        className="grid grid-cols-1 lg:grid-cols-[260px_1fr] lg:grid-rows-[minmax(0,1fr)] lg:flex-1 lg:min-h-0 gap-4 items-start"
      >
        {/* Ein langer Baum scrollt in sich, statt die Seite zu verlaengern. */}
        <div className="lg:max-h-full lg:overflow-y-auto">
          <FolderTree
            tree={tree}
            selectedId={effectiveId}
            onSelect={selectFolder}
            countsById={countsById}
            totalCount={allItems.length}
            showAll={!hauptordner}
            expandSelected={!!hauptordner}
            readOnly
          />
        </div>

        <div className="min-w-0 space-y-3 lg:h-full lg:min-h-0 lg:flex lg:flex-col">
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Händler-Nr., Produktname, SKU…"
          />

          {selected.size > 0 && (
            <div className="relative flex items-center gap-3 px-4 py-2 rounded-md bg-primary-soft text-primary text-sm font-medium flex-wrap">
              <strong>{selected.size}</strong> Artikel ausgewählt
              <span className="ml-auto flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => setShowPricingSheet(true)}
                >
                  <Tag className="size-3.5" />
                  Preise anpassen…
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  onClick={() => applyStatus(true)}
                  disabled={statusPending}
                >
                  <Power className="size-3.5" />
                  Aktivieren
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 text-coral border-coral/40 hover:bg-coral/10"
                  onClick={() => applyStatus(false)}
                  disabled={statusPending}
                >
                  <Power className="size-3.5" />
                  Deaktivieren
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelected(new Set())}
                >
                  Auswahl aufheben
                </Button>
              </span>
            </div>
          )}

          {isError && (
            <p className="text-sm text-coral">Fehler beim Laden der Artikel.</p>
          )}

          <div
            data-testid="artikel-liste"
            className="rounded-md border border-line bg-panel overflow-x-auto lg:overflow-auto lg:min-h-0"
          >
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-panel shadow-[0_1px_0_var(--line)]">
                <tr className="text-xs uppercase text-muted">
                  <th className="w-8 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={
                        folderItems.length > 0 && selected.size === folderItems.length
                      }
                      onChange={toggleAll}
                      className="size-3.5"
                    />
                  </th>
                  <th className="text-left font-medium px-2 py-2 whitespace-nowrap">Händler-Nr.</th>
                  <th className="text-left font-medium px-2 py-2">Produkt</th>
                  <th className="text-right font-medium px-2 py-2">Bestand</th>
                  <th
                    className="text-right font-medium px-2 py-2 whitespace-nowrap"
                    title="Verkaufte Stück der letzten 30 Tage, Grundartikel mit allen Varianten und Sets, ohne Storno, Gutschriften abgezogen. Balken je 5 Tage: grau die 30 Tage davor, farbig die letzten 30 Tage."
                  >
                    Verkauf 30 T.
                  </th>
                  <th className="text-right font-medium px-2 py-2" title="Einkaufspreis">EK</th>
                  <th className="text-left font-medium px-2 py-2">Status</th>
                  {rolloutErlaubt && <th className="w-20 min-w-20 text-right font-medium px-2 py-2">Rollout</th>}
                  <th className="text-left font-medium px-2 py-2">Ordner</th>
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <tr>
                    <td colSpan={rolloutErlaubt ? 9 : 8} className="text-center py-8 text-ink-2">
                      Lade…
                    </td>
                  </tr>
                ) : folderItems.length === 0 ? (
                  <tr>
                    <td colSpan={rolloutErlaubt ? 9 : 8} className="text-center py-8 text-ink-2">
                      {search.trim()
                        ? `Keine Treffer für „${search.trim()}“ in diesem Ordner.`
                        : "Noch keine aktiven Grundartikel in diesem Ordner."}
                    </td>
                  </tr>
                ) : (
                  visibleItems.map((item, index) => {
                    const rolloutQuery = rolloutQueries[Math.floor(index / 20)];
                    const rollout = rolloutQuery?.data?.[item.sku];
                    const ss = stockState(item);
                    return (
                      <tr
                        key={item.id}
                        className="border-b border-line-2 last:border-0 hover:bg-panel-2 cursor-pointer"
                        onClick={() => {
                          navigate(`/catalog/${encodeURIComponent(item.sku)}`, { state: katalogHerkunft() });
                        }}
                      >
                        <td
                          className="px-3 py-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(item.sku)}
                            onChange={() => toggleSelect(item.sku)}
                            className="size-3.5"
                          />
                        </td>
                        <td className="px-2 py-2 font-mono text-[13px] font-medium text-ink whitespace-nowrap">
                          <GrundartikelLink code={item.merchant_sku} state={katalogHerkunft()} />
                        </td>
                        <td className="px-2 py-2 text-ink w-full max-w-0 min-w-[240px]">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="inline-flex size-5 shrink-0">
                              {photos.data?.enabled !== false && (
                                <ProductPhotoHint images={photosBySku.get(item.sku)} alt={item.name ?? item.sku} />
                              )}
                            </span>
                            <span className="truncate" title={item.name ?? undefined}>{item.name}</span>
                            <AusverkaufAbzeichen {...item} />
                          </div>
                        </td>
                        <td className="px-2 py-2 text-right font-mono whitespace-nowrap">
                          <BestandMitVorab quantity={item.quantity} vorabOffen={item.vorab_offen} vorabEintraege={item.vorab_eintraege} />
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          <VerkaufZelle sales={salesById.get(item.id)} pending={sales.isPending} />
                        </td>
                        <td className="px-2 py-2 text-right font-mono whitespace-nowrap">
                          {fmtEK(item.supplier_price)}
                        </td>
                        <td className="px-2 py-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${STOCK_PILL_CLASSES[ss.kind]}`}
                          >
                            <span className="inline-block size-1.5 rounded-full bg-current" />
                            {ss.label}
                          </span>
                        </td>
                        {rolloutErlaubt && <td className="w-20 min-w-20 px-2 py-2 text-right font-mono whitespace-nowrap">
                          {rolloutQuery?.isPending ? '…' : rollout ? (rollout.aktiv > 0
                            ? <span title={`${rollout.ausgerollt} von ${rollout.aktiv} aktiven Angeboten ausgerollt, ${rollout.am_ziel} am Ziel`}>{Math.round(100 * rollout.ausgerollt / rollout.aktiv)} %</span>
                            : '—') : <span title="Rollout konnte nicht geladen werden">—</span>}
                        </td>}
                        <td
                          className="px-2 py-2 text-xs text-ink-2 truncate max-w-[200px]"
                          title={itemPath(item).map(prettyFolderLabel).join(" / ") || undefined}
                        >
                          {itemPath(item).map(prettyFolderLabel).join(" / ") || "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
                {folderItems.length > RENDER_CAP && (
                  <tr>
                    <td colSpan={rolloutErlaubt ? 9 : 8} className="text-center py-3 text-xs text-muted bg-panel-2">
                      Zeige {RENDER_CAP.toLocaleString("de-DE")} von{" "}
                      {folderItems.length.toLocaleString("de-DE")} — Suche oder Ordner verfeinern,
                      um weitere Treffer zu sehen.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Sheet open={showPricingSheet} onOpenChange={setShowPricingSheet}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Preise anpassen</SheetTitle>
            <SheetDescription>
              {selected.size} Artikel ausgew&auml;hlt — Anpassung wird sofort wirksam.
            </SheetDescription>
          </SheetHeader>

          <div className="px-4 py-2 space-y-4">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPricingMode("delta_pct")}
                className={`flex-1 px-3 py-2 rounded-md border text-sm ${
                  pricingMode === "delta_pct"
                    ? "border-primary bg-primary-soft text-primary font-semibold"
                    : "border-line text-ink-2 hover:border-line-2"
                }`}
              >
                Prozentual
              </button>
              <button
                type="button"
                onClick={() => setPricingMode("fixed")}
                className={`flex-1 px-3 py-2 rounded-md border text-sm ${
                  pricingMode === "fixed"
                    ? "border-primary bg-primary-soft text-primary font-semibold"
                    : "border-line text-ink-2 hover:border-line-2"
                }`}
              >
                Festpreis
              </button>
            </div>

            {pricingMode === "delta_pct" ? (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted font-semibold">
                  Aenderung in % (z.B. +10 oder -5)
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  value={pricingDelta}
                  onChange={(e) => setPricingDelta(e.target.value)}
                  placeholder="0"
                  className="mt-1"
                />
                <p className="text-[11px] text-muted mt-2">
                  Positiv erhoeht die Preise, negativ reduziert sie.
                </p>
              </div>
            ) : (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted font-semibold">
                  Neuer Festpreis (EUR)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={pricingFixed}
                  onChange={(e) => setPricingFixed(e.target.value)}
                  placeholder="0.00"
                  className="mt-1"
                />
                <p className="text-[11px] text-muted mt-2">
                  Setzt den Verkaufspreis fuer alle ausgewaehlten Artikel auf diesen Wert.
                </p>
              </div>
            )}
          </div>

          <SheetFooter>
            <Button variant="outline" onClick={() => setShowPricingSheet(false)}>
              Abbrechen
            </Button>
            <Button onClick={applyPricing} disabled={pricingPending}>
              {pricingPending ? "Wird angewendet…" : "Anwenden"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
